import { pick, isDemoSourced, eventStartTs } from './onboarding-beats-shared';

export interface PlanRow { icon: string; text: string }
const DAY = 86_400_000;
const MAX_ROWS = 8;
const MAX_PAST = 3;

/**
 * Calendar read for the Analysis beat's "what you're up to": upcoming events
 * (today → +60d) lead, with at most a few recent-past entries (−30d → today)
 * appended after — leading with a month of stale history buried the user's
 * actual plans.
 */
export function buildPlansRows(items: Array<Record<string, unknown>>): PlanRow[] {
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  const today = startOfToday.getTime();
  const lo = today - 30 * DAY;
  const hi = today + 60 * DAY;
  const dated = items
    .filter((o) => !isDemoSourced(o))
    .map((o) => ({ o, t: eventStartTs(String(pick(o, 'startAt') || pick(o, 'start') || '')) }))
    .filter(({ t }) => Number.isFinite(t) && t >= lo && t <= hi)
    .sort((a, b) => a.t - b.t);
  const upcoming = dated.filter(({ t }) => t >= today);
  // Most recent past first, capped — context, not history.
  const recentPast = dated.filter(({ t }) => t < today).reverse().slice(0, MAX_PAST);

  const seen = new Set<string>();
  const rows: PlanRow[] = [];
  for (const { o, t } of [...upcoming, ...recentPast]) {
    const title = String(pick(o, 'title') ?? 'Event').trim();
    const day = new Date(t).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
    const key = `${day}|${title.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({ icon: 'cal', text: `${day} — ${title}` });
    if (rows.length >= MAX_ROWS) break;
  }
  return rows;
}

export interface Trip {
  destination: string;
  dateRange: string;
  city?: string;
  lodging?: string;
  /** Real trip dates (ISO) — preferred for past/upcoming classification. */
  startISO?: string;
  endISO?: string;
}

/**
 * Classify discovery travelTrips into upcoming vs recent — the job emits a
 * flat list with year-less dateRange strings ("Mar 23", "Jun 25–26"), so the
 * card was blindly calling the first one "Upcoming" (a March trip in June).
 * Parses the FIRST month+day in the range against today; a date more than
 * ~6 months in the past is assumed to be next year (Dec → Jan boundary).
 * Unparseable dates are treated as recent (never falsely promised as a plan).
 */
export function classifyTrips(trips: Trip[], now = new Date()): { upcoming: Trip[]; recent: Trip[] } {
  const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const upcoming: Trip[] = [];
  const recent: Trip[] = [];
  for (const t of trips) {
    // Real dates when the job provides them — the yearless dateRange parse
    // below misreads "Jul 8" from LAST July as next July once the scan
    // window exceeds ~6 months.
    if (t.startISO) {
      const d = new Date(t.startISO).getTime();
      if (!Number.isNaN(d)) {
        ((t.endISO ? new Date(t.endISO).getTime() : d) >= today ? upcoming : recent).push(t);
        continue;
      }
    }
    const m = /([A-Za-z]{3,9})\s+(\d{1,2})/.exec(t.dateRange ?? '');
    const monthIdx = m ? MONTHS.indexOf(m[1].slice(0, 3).toLowerCase()) : -1;
    if (monthIdx === -1) { recent.push(t); continue; }
    let d = new Date(now.getFullYear(), monthIdx, Number(m![2])).getTime();
    if (d < today - 180 * DAY) d = new Date(now.getFullYear() + 1, monthIdx, Number(m![2])).getTime();
    (d >= today ? upcoming : recent).push(t);
  }
  return { upcoming, recent };
}

/**
 * Infer the user's home location from calendar data alone (the only data the
 * user has shared at onboarding time). Deterministic ladder:
 *   1. venue clustering — the most repeated city-looking segment in event
 *      `location` address strings ("Venue, 123 St, Santa Monica, CA 90401")
 *   2. dominant event timezone → region ("America/Los_Angeles" → "Los Angeles area")
 * Returns null when neither signal is confident.
 */
export function inferLocation(items: Array<Record<string, unknown>>): string | null {
  const events = items.filter((o) => !isDemoSourced(o));
  // ── Rung 1: address-segment clustering ────────────────────────────────
  const cityCounts = new Map<string, number>();
  for (const e of events) {
    const loc = String(pick(e, 'location') ?? '').trim();
    if (!loc) continue;
    const segs = loc.split(',').map((s) => s.trim());
    for (let i = 0; i < segs.length; i++) {
      // A city segment: 1-3 capitalized words, no digits, often followed by
      // a state/zip segment. Skip the first segment (venue name).
      const s = segs[i].replace(/\s+\d{4,}.*$/, '').trim();
      if (i === 0 || !s || /\d/.test(s)) continue;
      if (!/^[A-Z][a-zA-Z'.-]*(?: [A-Z][a-zA-Z'.-]*){0,2}$/.test(s)) continue;
      const next = (segs[i + 1] ?? '').trim();
      const state = /^[A-Z]{2}(?:\s+\d{5})?$/.test(next) ? next.slice(0, 2) : '';
      const key = state ? `${s}, ${state}` : s;
      cityCounts.set(key, (cityCounts.get(key) ?? 0) + 1);
    }
  }
  let topCity: string | null = null;
  let topCount = 0;
  for (const [city, n] of cityCounts) {
    if (n > topCount) { topCity = city; topCount = n; }
  }
  if (topCity && topCount >= 2) return topCity;

  // ── Rung 2: dominant timezone → region ────────────────────────────────
  const tzCounts = new Map<string, number>();
  for (const e of events) {
    const tz = String(pick(e, 'timezone') ?? '').trim();
    if (tz.includes('/')) tzCounts.set(tz, (tzCounts.get(tz) ?? 0) + 1);
  }
  let topTz = ''; let tzN = 0;
  for (const [tz, n] of tzCounts) if (n > tzN) { topTz = tz; tzN = n; }
  if (topTz && tzN >= 3) {
    const city = topTz.split('/').pop()!.replace(/_/g, ' ');
    return `${city} area`;
  }
  return null;
}
